import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocument, useFirestoreMutations } from '../../hooks/useFirestore'
import ImageUploader from './ImageUploader'
import RichTextEditor from './RichTextEditor'
import './AdminEditor.css'

const CATEGORIES = [
  { id: 'history', label: 'History' },
  { id: 'locations', label: 'Locations' },
  { id: 'factions', label: 'Factions' },
  { id: 'deities', label: 'Deities' },
  { id: 'items', label: 'Items' },
  { id: 'misc', label: 'Miscellaneous' }
]

const EMPTY_ARTICLE = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  category: 'misc',
  coverImageUrl: '',
  hidden: false,
  sortOrder: 0
}

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export default function LoreEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingArticle, loading } = useDocument('lore', isNew ? null : id)
  const { add, update, remove } = useFirestoreMutations('lore')

  const [article, setArticle] = useState(EMPTY_ARTICLE)
  const [autoSlug, setAutoSlug] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (existingArticle) {
      setArticle({ ...EMPTY_ARTICLE, ...existingArticle })
      setAutoSlug(false)
    }
  }, [existingArticle])

  const handleChange = (field, value) => {
    setArticle(prev => {
      const updated = { ...prev, [field]: value }

      // Auto-generate slug from title
      if (field === 'title' && autoSlug) {
        updated.slug = slugify(value)
      }

      return updated
    })
  }

  const handleSlugChange = (value) => {
    setAutoSlug(false)
    setArticle(prev => ({ ...prev, slug: slugify(value) }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      // Ensure slug exists
      const data = {
        ...article,
        slug: article.slug || slugify(article.title)
      }

      if (isNew) {
        await add(data)
      } else {
        await update(id, data)
      }

      navigate('/admin/lore')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      await remove(id)
      navigate('/admin/lore')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Article' : 'Edit Article'}</h1>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate('/admin/lore')}
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            id="title"
            type="text"
            value={article.title}
            onChange={(e) => handleChange('title', e.target.value)}
            required
            placeholder="Article title"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="slug">URL Slug</label>
            <input
              id="slug"
              type="text"
              value={article.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="url-friendly-name"
            />
            <p className="help-text">/lore/{article.slug || 'url-slug'}</p>
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={article.category}
              onChange={(e) => handleChange('category', e.target.value)}
            >
              {CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="excerpt">Excerpt</label>
          <textarea
            id="excerpt"
            rows={3}
            value={article.excerpt}
            onChange={(e) => handleChange('excerpt', e.target.value)}
            placeholder="Brief summary shown in article listings..."
          />
        </div>

        <div className="form-group">
          <label>Cover Image</label>
          <ImageUploader
            value={article.coverImageUrl}
            onChange={(url) => handleChange('coverImageUrl', url)}
            folder="lore"
          />
        </div>

        <div className="form-group">
          <label>Content</label>
          <RichTextEditor
            value={article.content}
            onChange={(html) => handleChange('content', html)}
            placeholder="Write your lore article..."
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="sortOrder">Sort Order</label>
            <input
              id="sortOrder"
              type="number"
              value={article.sortOrder}
              onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
              style={{ maxWidth: '100px' }}
            />
          </div>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={article.hidden === true}
                onChange={(e) => handleChange('hidden', e.target.checked)}
              />
              <span>Hidden (Spoiler)</span>
            </label>
            <span className="help-text">Only visible in admin</span>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Article' : 'Save Changes')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Article
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
